import { MultiPolygon, Polygon } from "geojson";
import _ from "lodash";
import { createSelectorCreator, defaultMemoize } from "reselect";

import { Comment, CommentFilters, Store } from "fond/types";
import { filterFeaturesByGeometry } from "fond/utils/feature";

// Creates a selection that memoizes & uses deep equals
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, _.isEqual);

/**
 * Returns all comments within redux as an array of comments
 */
export const getAll = createDeepEqualSelector(
  (state: Store) => state.comments.items,
  (comments) => Object.values(comments)
);

/**
 * Returns a comment matching the ID
 * @param id the id of the item to find parents of
 */
export const getOne = createDeepEqualSelector(
  (state: Store): { [key: string]: Comment } => state.comments.items,
  (comments) => (commentId?: string) => comments[commentId || ""]
);

/**
 * Returns all comments of a version
 */
export const getByVersion = createDeepEqualSelector(getAll, (comments) => (versionId?: string) => {
  return comments.filter((comment) => comment.Version === versionId);
});

/**
 * Returns all unresolved comments of a project
 */
export const getUnresolved = createDeepEqualSelector(getAll, (comments) => comments.filter((comment) => comment.State !== "resolved"));

/**
 * Returns all unresolved comments of a project
 */
export const getResolved = createDeepEqualSelector(getAll, (comments) => comments.filter((comment) => comment.State === "resolved"));

/**
 * Returns an object of each comment layers count based on resolution state.
 * When a selection geometry is provided, only comments with a feature intersecting it are counted.
 *
 * example:
 * {
 *     point: 1,
 *     polygon: 2,
 *     lineString: 3,
 *     arrowLine: 4,
 *     project: 5,
 *   }
 */
export const getTypeCountByResolutionState = createDeepEqualSelector(getAll, (comments) =>
  _.memoize(
    (filters: CommentFilters, selectionGeometry?: Polygon | MultiPolygon | null) => {
      const result = {
        "comments-point": { count: 0, length: null, preference: "count" as const },
        "comments-polygon": { count: 0, length: null, preference: "count" as const },
        "comments-lineString": { count: 0, length: null, preference: "count" as const },
        "comments-arrowLine": { count: 0, length: null, preference: "count" as const },
        "comments-project": { count: 0, length: null, preference: "count" as const },
      };

      comments.forEach((comment) => {
        if (
          (filters.State.length === 0 || filters.State.indexOf(comment.State) > -1) &&
          (filters.Importance.length === 0 || filters.Importance.indexOf(comment.Importance) > -1) &&
          (!selectionGeometry || filterFeaturesByGeometry(comment.Features, selectionGeometry).length > 0)
        ) {
          result[`comments-${comment.Type}`].count += 1;
        }
      });

      return result;
    },
    // lodash's default resolver caches on the first argument alone, so the geometry
    // has to be part of the key.
    (filters, selectionGeometry) => JSON.stringify([filters, selectionGeometry])
  )
);
